import { useState, useCallback, useMemo } from 'react';

/**
 * Hook for week-based calendar navigation.
 * Returns the 7 days (Mon–Sun) of the current week, with prev/next navigation.
 */
export function useCalendarWeek() {
  // State holds the Monday of the active week
  const [monday, setMonday] = useState(() => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun, 1=Mon, ...
    const diff = day === 0 ? -6 : 1 - day;
    const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff);
    return mon;
  });

  const prevWeek = useCallback(() => {
    setMonday(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  }, []);

  const nextWeek = useCallback(() => {
    setMonday(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  }, []);

  const goToToday = useCallback(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    setMonday(new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff));
  }, []);

  const weekDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [monday]);

  const weekLabel = useMemo(() => {
    const sun = new Date(monday);
    sun.setDate(sun.getDate() + 6);
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const startStr = monday.toLocaleDateString('en-US', opts);
    const endStr = sun.toLocaleDateString('en-US', {
      ...opts,
      year: 'numeric',
    });
    return `${startStr} – ${endStr}`;
  }, [monday]);

  return { weekDays, prevWeek, nextWeek, goToToday, weekLabel } as const;
}
